import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { applicationService } from '../services/applicationService';
import { fieldInspectionService } from '../services/fieldInspectionService';

// Hook for creating new applications
export const useCreateApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationData, images = [] }) => {
      // Validate data before sending
      applicationService.validateApplicationData(applicationData);
      return applicationService.applyExgratia(applicationData, images);
    },
    onSuccess: (data) => {
      toast.success('Application submitted successfully!');
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['user-applications']);
      return data;
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.message || 'Failed to submit application';
      toast.error(message);
      throw error;
    },
  });
};

// Hook for getting applications with pagination and filters
export const useApplications = (params = {}) => {
  return useQuery({
    queryKey: ['applications', params],
    queryFn: () => applicationService.getApplications(params),
    keepPreviousData: true,
    staleTime: Infinity,
  });
};

export const useDamageInspectionRequests = (params = {}) => {
  return useQuery({
    queryKey: ['damage-inspection-requests', params],
    queryFn: () => applicationService.getApplications({ ...params, type: 'damage_inspection' }),
    keepPreviousData: true,
    staleTime: Infinity,
  });
};

// Hook for getting user's applications
export const useUserApplications = (params = {}) => {
  return useQuery({
    queryKey: ['user-applications', params],
    queryFn: () => applicationService.getUserApplications(params),
    staleTime: Infinity,
  });
};

// Hook for getting single application
export const useApplication = (getBy, applicationRefno, options = {}) => {
  return useQuery({
    queryKey: ['application', getBy, applicationRefno],
    queryFn: () => applicationService.getApplicationByRef(getBy, applicationRefno),
    enabled: !!applicationRefno,
    ...options,
  });
};

// Hook for editing applications
export const useEditApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationRefno, applicationData }) => {
      applicationService.validateApplicationData(applicationData);
      return applicationService.editApplication(applicationRefno, applicationData);
    },
    onSuccess: (data, variables) => {
      toast.success('Application updated successfully!');
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['user-applications']);
      queryClient.invalidateQueries(['application', 'refno', variables.applicationRefno]);
      return data;
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.message || 'Failed to update application';
      toast.error(message);
      throw error;
    },
  });
};

// Hook for application statistics
export const useApplicationStats = (params = {}) => {
  return useQuery({
    queryKey: ['application-stats', params],
    queryFn: () => applicationService.getApplicationStats(params),
    staleTime: Infinity
  });
};

// Hook for workflow operations
export const useWorkflowActions = () => {
  const queryClient = useQueryClient();

  const forwardMutation = useMutation({
    mutationFn: ({ id, workflowData }) => applicationService.forwardApplication(id, workflowData),
    onSuccess: () => {
      toast.success('Application forwarded successfully');
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['user-applications']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to forward application');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, rejectionData }) => applicationService.rejectApplication(id, rejectionData),
    onSuccess: () => {
      toast.success('Application rejected');
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['user-applications']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject application');
    },
  });

  const completeMutation = useMutation({
    mutationFn: ({ id, completionData }) => applicationService.completeWorkflowStep(id, completionData),
    onSuccess: () => {
      toast.success('Workflow step completed');
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['user-applications']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to complete workflow step');
    },
  });

  return {
    forward: forwardMutation,
    reject: rejectMutation,
    complete: completeMutation,
  };
};

// Hook for getting workflow history
export const useWorkflowHistory = (applicationId) => {
  return useQuery({
    queryKey: ['workflow-history', applicationId],
    queryFn: () => applicationService.getWorkflowHistory(applicationId),
    enabled: !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
};

// Field Inspection Hooks
export const useInspectionReports = (type, params = {}) => {
  return useQuery({
    queryKey: ['inspection-reports', type, params],
    queryFn: () => fieldInspectionService.getInspectionReports(type, params),
    keepPreviousData: true,
    staleTime: Infinity,
  });
};

export const useNewInspectionReports = (params = {}) => {
  return useQuery({
    queryKey: ['new-inspection-reports', params],
    queryFn: () => fieldInspectionService.getNewReports(params),
    keepPreviousData: true,
    staleTime: Infinity,
    ...params, // This allows passing enabled: false
  });
};

export const useAssignedInspectionReports = (type = 'head') => {
  return useQuery({
    queryKey: ['assigned-inspection-reports', type],
    queryFn: () => fieldInspectionService.getAssignedReports(type),
    keepPreviousData: true,
    staleTime: Infinity,
  });
};

export const useUnverifiedInspectionReports = (params = {}) => {
  return useQuery({
    queryKey: ['unverified-inspection-reports', params],
    queryFn: () => fieldInspectionService.getUnverifiedReports(params),
    keepPreviousData: true,
    staleTime: Infinity,
    ...params, // This allows passing enabled: false
  });
};

export const usePendingInspectionReports = (params = {}) => {
  return useQuery({
    queryKey: ['pending-inspection-reports', params],
    queryFn: () => fieldInspectionService.getPendingReports(params),
    keepPreviousData: true,
    staleTime: Infinity,
    ...params, // This allows passing enabled: false
  });
};

export const useVerifiedInspectionReports = (params = {}) => {
  return useQuery({
    queryKey: ['verified-inspection-reports', params],
    queryFn: () => fieldInspectionService.getVerifiedReports(params),
    keepPreviousData: true,
    staleTime: Infinity,
    ...params, // This allows passing enabled: false
  });
};

export const useInspectionReportById = (reportId) => {
  return useQuery({
    queryKey: ['inspection-report', reportId],
    queryFn: () => fieldInspectionService.getReportById(reportId),
    enabled: !!reportId,
  });
};

// Field Inspection Actions
export const useInspectionReportActions = () => {
  const queryClient = useQueryClient();

  const assignMutation = useMutation({
    mutationFn: ({ id, assignmentData }) => applicationService.assignFieldInspectionOfficial(id, assignmentData),
    onSuccess: () => {
      toast.success('Report assigned successfully');
      queryClient.invalidateQueries(['field-inspection-reports']);
      queryClient.invalidateQueries(['new-inspection-reports']);
      queryClient.invalidateQueries(['pending-inspection-reports']);
      queryClient.invalidateQueries(['application']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to assign report');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: ({ reportId, verificationData }) => fieldInspectionService.verifyReport(reportId, verificationData),
    onSuccess: () => {
      console.log('Report verified successfully');
      toast.success('Report verification completed');
      queryClient.invalidateQueries(['field-inspection-reports']);
      queryClient.invalidateQueries(['inspection-reports']);
      queryClient.invalidateQueries(['unverified-inspection-reports']);
      queryClient.invalidateQueries(['verified-inspection-reports']);
      queryClient.invalidateQueries(['application']);
      queryClient.invalidateQueries(['workflow-history']);
    },
    onError: (error) => {
      console.error('Error verifying report:', error);
      toast.error(error.response?.data?.message || 'Failed to verify report');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ reportId, reportData }) => fieldInspectionService.uploadReport(reportId, reportData),
    onSuccess: () => {
      toast.success('Report uploaded successfully');
      queryClient.invalidateQueries(['field-inspection-reports']);
      queryClient.invalidateQueries(['pending-inspection-reports']);
      queryClient.invalidateQueries(['unverified-inspection-reports']);
      queryClient.invalidateQueries(['application']);
      queryClient.invalidateQueries(['workflow-history']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to upload report');
    },
  });

  return {
    assign: assignMutation,
    verify: verifyMutation,
    upload: uploadMutation,
  };
};
